/**
 * WindVisualization.tsx
 * 
 * Animated wind particle visualization overlay for the map
 * Shows wind direction and speed using flowing particles
 * Critical for oilfield worker safety - high winds affect crane ops, driving
 */

import { useEffect, useRef, useState, useCallback } from 'react';

interface WindData {
  speed: number; // mph
  direction: number; // degrees (meteorological: where wind comes FROM)
  gust?: number;
  cardinal: string;
}

interface WindVisualizationProps {
  windData: WindData | null;
  mapBounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  isVisible?: boolean;
  intensity?: 'low' | 'medium' | 'high';
}

interface Beam {
  x: number;
  y: number;
  length: number;
  width: number;
  speed: number;
  opacity: number;
  phase: number;
}

// Wind severity colors - neon beams
const getWindColor = (speed: number): string => {
  if (speed >= 50) return '#ff0000'; // Hurricane force
  if (speed >= 40) return '#ff4500'; // Severe
  if (speed >= 30) return '#ff8c00'; // High
  if (speed >= 20) return '#ffd700'; // Strong
  if (speed >= 10) return '#00ff88'; // Moderate
  return '#00ffff'; // Calm
};

export function WindVisualization({ 
  windData, 
  isVisible = true,
  intensity = 'medium'
}: WindVisualizationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | undefined>(undefined);
  const beamsRef = useRef<Beam[]>([]);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Calculate beam count based on wind speed and intensity setting
  const getBeamCount = useCallback((): number => {
    const baseCount = intensity === 'low' ? 30 : intensity === 'high' ? 90 : 60;
    const windFactor = Math.min((windData?.speed || 0) / 30, 1.5);
    return Math.floor(baseCount * (0.6 + windFactor));
  }, [windData?.speed, intensity]);

  // Initialize beams
  const initBeams = useCallback((width: number, height: number, beamCount: number) => {
    const beams: Beam[] = [];
    for (let i = 0; i < beamCount; i++) {
      beams.push({
        x: Math.random() * width,
        y: Math.random() * height,
        length: 50 + Math.random() * 120,
        width: 2 + Math.random() * 2,
        speed: 0.4 + Math.random() * 1.6,
        opacity: 0.25 + Math.random() * 0.55,
        phase: Math.random() * Math.PI * 2,
      });
    }
    beamsRef.current = beams;
  }, []);

  // Update canvas dimensions with proper pixel ratio handling
  useEffect(() => {
    const updateDimensions = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const parent = canvas.parentElement;
        if (parent) {
          const rect = parent.getBoundingClientRect();
          // Use device pixel ratio for sharp rendering
          const dpr = window.devicePixelRatio || 1;
          
          // Set display size
          canvas.style.width = `${rect.width}px`;
          canvas.style.height = `${rect.height}px`;
          
          // Set actual canvas buffer size (scaled by DPR)
          canvas.width = Math.floor(rect.width * dpr);
          canvas.height = Math.floor(rect.height * dpr);
          
          // Scale context to match DPR
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.scale(dpr, dpr);
          }
          
          setDimensions({ width: rect.width, height: rect.height });
          initBeams(rect.width, rect.height, getBeamCount());
        }
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [initBeams, getBeamCount]);

  // Animation loop
  useEffect(() => {
    if (!isVisible || !windData || windData.speed < 1) {
      // Clear canvas when not animating
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = undefined;
      }
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Use willReadFrequently for better performance on repeated reads
    const ctx = canvas.getContext('2d', { 
      alpha: true,
      willReadFrequently: false,
    });
    if (!ctx) return;

    const { width, height } = dimensions;
    if (width === 0 || height === 0) return;

    // Convert meteorological wind direction (where it comes FROM) to canvas heading (where it blows TO)
    // Canvas coordinates: 0 rad points right (east), Y increases downward. A wind FROM north should move downward.
    const windRadians = ((windData.direction + 90) * Math.PI) / 180;
    const windSpeedFactor = Math.min(windData.speed / 20, 3); // visual speed cap
    const color = getWindColor(windData.speed);

    const dpr = window.devicePixelRatio || 1;
    
    const animate = () => {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.restore();

      ctx.globalCompositeOperation = 'screen';

      beamsRef.current.forEach((beam, idx) => {
        const moveX = Math.cos(windRadians) * beam.speed * windSpeedFactor;
        const moveY = Math.sin(windRadians) * beam.speed * windSpeedFactor;

        beam.x += moveX;
        beam.y += moveY;

        // Gentle shimmer
        beam.phase += 0.02 + (windSpeedFactor * 0.01);
        const shimmer = 0.5 + Math.sin(beam.phase) * 0.25;

        // Wrap around edges with padding
        if (beam.x < -60) beam.x = width + 60;
        if (beam.x > width + 60) beam.x = -60;
        if (beam.y < -60) beam.y = height + 60;
        if (beam.y > height + 60) beam.y = -60;

        const heading = Math.atan2(moveY, moveX);

        ctx.save();
        ctx.translate(beam.x, beam.y);
        ctx.rotate(heading);

        const grad = ctx.createLinearGradient(-beam.length / 2, 0, beam.length / 2, 0);
        grad.addColorStop(0, `${color}00`);
        grad.addColorStop(0.2, `${color}55`);
        grad.addColorStop(0.5, `${color}${Math.floor(beam.opacity * 255).toString(16).padStart(2, '0')}`);
        grad.addColorStop(0.8, `${color}55`);
        grad.addColorStop(1, `${color}00`);

        ctx.strokeStyle = grad;
        ctx.lineWidth = beam.width * (1 + shimmer * 0.3);
        ctx.shadowColor = color;
        ctx.shadowBlur = 14 * (0.7 + shimmer * 0.6);
        ctx.lineCap = 'round';

        ctx.beginPath();
        ctx.moveTo(-beam.length / 2, 0);
        ctx.lineTo(beam.length / 2, 0);
        ctx.stroke();
        ctx.restore();

        // Gust jitter for every few beams
        if (windData.gust && windData.gust > windData.speed && idx % 5 === 0) {
          const gustFactor = (windData.gust - windData.speed) / 40;
          beam.x += (Math.random() - 0.5) * gustFactor * 6;
          beam.y += (Math.random() - 0.5) * gustFactor * 6;
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [windData, isVisible, dimensions]);

  if (!windData || !isVisible) return null;

  return (
    <>
      {/* Canvas for particles - just the animated wind visualization, no badge */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 10,
          // Use simpler rendering - avoid GPU-forcing transforms that cause grid artifacts
          opacity: 0.999, // Trick to create compositing layer without transform3d issues
        }}
      />
    </>
  );
}

export default WindVisualization;
